/** Shared helpers for support chat image/video pick, paste, and drop. */

export function isSupportMediaFile(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  if (type.startsWith('image/')) return true;
  if (type.startsWith('video/')) {
    return (
      type.includes('mp4') ||
      type.includes('webm') ||
      type.includes('quicktime') ||
      /\.(mp4|webm|mov)$/i.test(file.name)
    );
  }
  // Clipboard / OS drag often omit MIME — fall back to extension.
  return /\.(jpe?g|png|gif|webp|bmp|heic|mp4|webm|mov)$/i.test(file.name);
}

export function filesFromFileList(list: FileList | null | undefined): File[] {
  if (!list?.length) return [];
  return Array.from(list).filter(isSupportMediaFile);
}

export function filesFromDataTransfer(dt: DataTransfer | null | undefined): File[] {
  if (!dt) return [];
  if (dt.files?.length) return filesFromFileList(dt.files);
  const out: File[] = [];
  if (dt.items?.length) {
    for (let i = 0; i < dt.items.length; i++) {
      const item = dt.items[i];
      if (item.kind !== 'file') continue;
      const f = item.getAsFile();
      if (f && isSupportMediaFile(f)) out.push(f);
    }
  }
  return out;
}

export function filesFromClipboard(clipboardData: DataTransfer | null | undefined): File[] {
  return filesFromDataTransfer(clipboardData);
}

export type PendingMediaKind = 'image' | 'video' | 'file';

export type PendingMediaItem = {
  file: File;
  previewUrl: string | null;
  kind: PendingMediaKind;
};

function pendingKindForFile(file: File): PendingMediaKind {
  const type = (file.type || '').toLowerCase();
  if (type.startsWith('video/') || /\.(mp4|webm|mov)$/i.test(file.name)) return 'video';
  if (type.startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp|heic)$/i.test(file.name)) return 'image';
  return 'file';
}

export function toPendingMedia(files: File[]): PendingMediaItem[] {
  return files.map((file) => {
    const kind = pendingKindForFile(file);
    return {
      file,
      kind,
      previewUrl: kind === 'image' || kind === 'video' ? URL.createObjectURL(file) : null,
    };
  });
}

/** Prefer mp4/aac on iOS Safari — webm blobs often won't play in <audio> before upload. */
export function pickVoiceRecorderMime(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = [
    'audio/mp4',
    'audio/aac',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
  ];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return '';
}

export function voiceFilenameForMime(mime: string): string {
  const m = (mime || '').toLowerCase();
  if (m.includes('mp4') || m.includes('m4a') || m.includes('aac')) return 'voice.m4a';
  if (m.includes('ogg')) return 'voice.ogg';
  if (m.includes('mpeg') || m.includes('mp3')) return 'voice.mp3';
  return 'voice.webm';
}

export function revokePendingMedia(items: PendingMediaItem[]) {
  for (const item of items) {
    if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
  }
}

/** Latest activity first; closed tickets sink to the bottom. */
export function sortSupportTickets<T extends { id: number; status: string; updated_at: string }>(
  items: T[],
  tsFn: (v: string) => number,
): T[] {
  return [...items].sort((a, b) => {
    const aClosed = a.status === 'closed' ? 1 : 0;
    const bClosed = b.status === 'closed' ? 1 : 0;
    if (aClosed !== bClosed) return aClosed - bClosed;
    const dt = tsFn(b.updated_at) - tsFn(a.updated_at);
    if (dt !== 0) return dt;
    return b.id - a.id;
  });
}

/**
 * Upsert ticket into list.
 * Re-sort only when status changes or updated_at is strictly newer — avoids WS/poll thrash.
 */
export function upsertSupportTicket<
  T extends {
    id: number;
    status: string;
    updated_at: string;
    last_message_preview?: string | null;
    last_message_id?: number | null;
    last_message_type?: string | null;
    last_message_media_count?: number | null;
    last_message_author_role?: string | null;
  },
>(prev: T[], row: T, tsFn: (v: string) => number): T[] {
  const old = prev.find((t) => t.id === row.id);
  if (old && tsFn(old.updated_at) > tsFn(row.updated_at)) {
    if (!old.last_message_preview && row.last_message_preview) {
      return prev.map((t) =>
        t.id === row.id ? { ...t, last_message_preview: row.last_message_preview } : t,
      );
    }
    return prev;
  }

  const sameStatus = Boolean(old && old.status === row.status);
  const sameTime = Boolean(old && tsFn(old.updated_at) === tsFn(row.updated_at));
  if (
    old &&
    sameStatus &&
    sameTime &&
    (old.last_message_preview || '') === (row.last_message_preview || '') &&
    (old.last_message_id ?? null) === (row.last_message_id ?? null) &&
    (old.last_message_author_role ?? null) === (row.last_message_author_role ?? null)
  ) {
    return prev;
  }

  if (old && sameStatus && tsFn(row.updated_at) <= tsFn(old.updated_at)) {
    return prev.map((t) => (t.id === row.id ? { ...t, ...row, updated_at: old.updated_at } : t));
  }

  const without = prev.filter((t) => t.id !== row.id);
  return sortSupportTickets([old ? { ...old, ...row } : row, ...without], tsFn);
}
